import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Barcode, Trash2, Flame, AlertTriangle, CircleDashed, CircleCheck, Download, Printer, Camera, Plus, TrendingUp, TrendingDown, ShoppingCart } from "lucide-react";
import { Label } from "@/components/ui/label";
import PurchaseDialog from "@/pages/PurchaseDialog";
import { toast } from "sonner";
import AddRequirementDialog from "@/pages/AddRequirementDialog";
import BarcodeScannerDialog from "@/pages/BarcodeScannerDialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PRIORITIES = [
  { value: "urgent", label: "Urgent", cls: "pill-urgent", icon: Flame },
  { value: "high", label: "High", cls: "pill-high", icon: AlertTriangle },
  { value: "normal", label: "Normal", cls: "pill-normal", icon: CircleCheck },
  { value: "low", label: "Low", cls: "pill-low", icon: CircleDashed },
];
const P_MAP = Object.fromEntries(PRIORITIES.map((p) => [p.value, p]));

function PriceAlert({ req }) {
  const p = req.product_snapshot || {};
  const last = Number(p.last_purchase_cost || 0);
  const lowest = Number(req.lowest_cost || 0);
  if (!last || !lowest || last <= 0) return null;
  if (Math.abs(last - lowest) < 0.01) {
    return (
      <div
        className="text-xs text-emerald-700 mt-1 flex items-center gap-1"
        data-testid={`price-alert-${req.id}`}
      >
        <TrendingDown className="w-3 h-3" />
        At your lowest ever (₹{lowest.toFixed(2)}).
      </div>
    );
  }
  if (last > lowest) {
    const pct = ((last - lowest) / lowest) * 100;
    return (
      <div
        className="text-xs text-[#9A3412] mt-1 flex items-center gap-1"
        data-testid={`price-alert-${req.id}`}
      >
        <TrendingUp className="w-3 h-3" />
        Last cost ₹{last.toFixed(2)} is {pct.toFixed(1)}% above your lowest
        (₹{lowest.toFixed(2)}
        {req.lowest_date ? ` on ${formatDate(req.lowest_date)}` : ""}
        ).
      </div>
    );
  }
  return null;
}

export default function Requirement() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseReqs, setPurchaseReqs] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // {mode:"one"|"many", ids:[...], previewName:"..."}
  const [deleting, setDeleting] = useState(false);
  const searchRef = useRef();

  const loadRequirements = async () => {
    setLoading(true);
    try {
      const params = { status: "requirement" };
      if (filter !== "all") params.priority = filter;
      const { data } = await api.get("/requirements", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Live typeahead: whatever is typed → search products (same as Inventory)
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setShowResults(false);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/products", { params: { q, limit: 20 } });
        setResults(data.items || []);
        setShowResults(true);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const pickProduct = (p) => {
    setSelectedProduct(p);
    setDialogOpen(true);
    setQuery("");
    setResults([]);
    setShowResults(false);
    searchRef.current?.focus();
  };

  const onScanned = async (code) => {
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(code.trim())}`);
      setSelectedProduct(data);
      setDialogOpen(true);
      toast.success(`Scanned: ${code}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportPo = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/requirements/export?status=requirement`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchase_order_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Purchase order downloaded");
    } catch (e) {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const printPo = () => {
    window.open("/requirement/print", "_blank", "noopener");
  };

  const updateField = async (id, patch) => {
    try {
      await api.put(`/requirements/${id}`, patch);
      loadRequirements();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const askRemoveOne = (r) => {
    setConfirmDelete({
      mode: "one",
      ids: [r.id],
      previewName: r.product_snapshot?.product || "this item",
    });
  };

  const askRemoveSelected = () => {
    if (!selectedIds.size) return;
    setConfirmDelete({
      mode: "many",
      ids: Array.from(selectedIds),
      previewName: "",
    });
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      if (confirmDelete.mode === "one") {
        await api.delete(`/requirements/${confirmDelete.ids[0]}`);
        toast.success("Removed");
      } else {
        const { data } = await api.post("/requirements/bulk-delete", {
          ids: confirmDelete.ids,
        });
        toast.success(`Removed ${data.deleted} of ${data.requested}`);
      }
      setSelectedIds((prev) => {
        const n = new Set(prev);
        confirmDelete.ids.forEach((id) => n.delete(id));
        return n;
      });
      setConfirmDelete(null);
      // Trigger sidebar count refresh
      window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
      loadRequirements();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const openPurchaseOne = (r) => {
    setPurchaseReqs([r]);
    setPurchaseOpen(true);
  };

  const openPurchaseSelected = () => {
    const reqs = items.filter((r) => selectedIds.has(r.id));
    if (!reqs.length) return;
    setPurchaseReqs(reqs);
    setPurchaseOpen(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((r) => r.id)));
  };

  return (
    <div className="fade-up" data-testid="requirement-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Restock list</p>
          <h1 className="font-heading text-3xl tracking-tight">Requirement</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Products to be reordered. Set quantity and priority, then move to purchased.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportPo}
            disabled={!items.length || exporting}
            data-testid="export-po-btn"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting…" : "Export PO"}
          </Button>
          <Button
            variant="outline"
            onClick={printPo}
            disabled={!items.length}
            data-testid="print-po-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Print PO
          </Button>

          <Button
            variant="outline"
            onClick={openPurchaseSelected}
            disabled={selectedIds.size === 0}
            data-testid="purchase-selected-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" /> Purchase selected ({selectedIds.size})
          </Button>
          <Button
            variant="outline"
            onClick={askRemoveSelected}
            disabled={selectedIds.size === 0}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="delete-selected-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete selected ({selectedIds.size})
          </Button>
        </div>
      </div>

      {/* Quick search (same behaviour as Inventory search) */}
      <Card className="p-5 mb-5" data-testid="quick-search-card">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Barcode className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9E6D5B]" />
            <Input
              ref={searchRef}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => query.trim() && setShowResults(true)}
              placeholder="Search by manual barcode, auto barcode, product, group or brand…"
              className="pl-10 h-12 text-base bg-white"
              data-testid="quick-search-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-12 px-4"
            onClick={() => setScannerOpen(true)}
            data-testid="open-scanner-btn"
          >
            <Camera className="w-4 h-4 mr-2" /> Scan
          </Button>
        </div>

        {showResults && (
          <div
            className="mt-3 border border-[hsl(var(--border))] rounded-md overflow-hidden bg-white"
            data-testid="quick-search-results"
          >
            {searching && results.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">Searching…</div>
            ) : results.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No products match your search.</div>
            ) : (
              <ul className="max-h-80 overflow-y-auto divide-y divide-[hsl(var(--border))]">
                {results.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => pickProduct(p)}
                      className="w-full text-left p-3 hover:bg-[hsl(var(--muted))] transition-colors flex items-start gap-3"
                      data-testid={`quick-pick-${p.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.product}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.brand_name || "—"} · {p.group_name || "—"}
                        </div>
                        <div className="text-xs font-mono-data text-muted-foreground mt-0.5">
                          {p.auto_barcode}
                          {p.manual_barcode && ` · ${p.manual_barcode}`}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono-data text-sm">₹{Number(p.mrp || 0).toFixed(2)}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">MRP</div>
                      </div>
                      <Plus className="w-4 h-4 text-[#9E6D5B] mt-1" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Filter</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="filter-priority-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => (
              <SelectItem key={p.value} value={p.value} data-testid={`filter-opt-${p.value}`}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">{items.length} item(s)</div>
      </div>

      <Card className="overflow-hidden" data-testid="requirement-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]">
              <tr className="text-left">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={selectedIds.size > 0 && selectedIds.size === items.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                    aria-label="Select all"
                  />
                </th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Product</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Brand / Group</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Barcodes</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Qty</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Priority</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const p = r.product_snapshot || {};
                const M = P_MAP[r.priority] || P_MAP.normal;
                return (
                  <tr
                    key={r.id}
                    className="data-row border-t border-[hsl(var(--border))] transition-colors"
                    data-testid={`requirement-row-${r.id}`}
                  >
                    <td className="p-3">
                      <Checkbox
                        checked={selectedIds.has(r.id)}
                        onCheckedChange={() => toggleSelect(r.id)}
                        data-testid={`select-checkbox-${r.id}`}
                        aria-label="Select row"
                      />
                    </td>
                    <td className="p-3 max-w-[320px]">
                      <div className="font-medium truncate" title={p.product}>{p.product}</div>
                      {r.notes && <div className="text-xs text-red-600 font-medium mt-0.5">{r.notes}</div>}
                      <PriceAlert req={r} />
                    </td>
                    <td className="p-3 text-sm">
                      <div>{p.brand_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{p.group_name || "—"}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-mono-data text-xs leading-tight">
                        <div>{p.auto_barcode}</div>
                        {p.manual_barcode && <div className="text-muted-foreground">{p.manual_barcode}</div>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <Input
                        type="number"
                        min={1}
                        value={r.quantity}
                        onChange={(e) => {
                          const v = Math.max(1, parseInt(e.target.value || "1"));
                          setItems((arr) => arr.map((x) => (x.id === r.id ? { ...x, quantity: v } : x)));
                        }}
                        onBlur={(e) => updateField(r.id, { quantity: Math.max(1, parseInt(e.target.value || "1")) })}
                        className="w-20 h-8 ml-auto text-right font-mono-data"
                        data-testid={`qty-input-${r.id}`}
                      />
                    </td>
                    <td className="p-3">
                      <Select
                        value={r.priority}
                        onValueChange={(v) => updateField(r.id, { priority: v })}
                      >
                        <SelectTrigger className="w-[130px] h-8" data-testid={`priority-select-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className={`pill ${M.cls} mt-1 ml-0.5 inline-flex`}>
                        <M.icon className="w-3 h-3" />
                        {M.label}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => openPurchaseOne(r)}
                          style={{ backgroundColor: "#9E6D5B" }}
                          data-testid={`purchase-btn-${r.id}`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Purchase
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => askRemoveOne(r)}
                          data-testid={`delete-req-btn-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!items.length && !loading && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-sm text-muted-foreground">
                    No requirements yet. Use the search above or go to Inventory to mark products.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AddRequirementDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        product={selectedProduct}
        onAdded={loadRequirements}
      />

      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onDetected={onScanned}
      />

      <PurchaseDialog
        open={purchaseOpen}
        onOpenChange={setPurchaseOpen}
        requirements={purchaseReqs}
        onDone={() => {
          setSelectedIds(new Set());
          // Trigger sidebar count refresh for requirements
          window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
          loadRequirements();
        }}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}
      >
        <AlertDialogContent data-testid="req-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.mode === "many"
                ? `Delete ${confirmDelete?.ids?.length ?? 0} requirements?`
                : "Delete this requirement?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.mode === "many" ? (
                <>These items will be permanently removed from your restock list. This cannot be undone.</>
              ) : (
                <>
                  <span className="font-medium text-foreground">{confirmDelete?.previewName}</span>
                  {" "}will be removed from your restock list. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="req-delete-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              disabled={deleting}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="req-delete-confirm"
            >
              {deleting ? "Deleting…" : confirmDelete?.mode === "many" ? "Delete all" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
